<template>
  <div>
    <DiscardPopup v-model="showDiscardPopup" @cancel="popupCancel" @discard="close" />
    <SendMessageInput
      v-model="message"
      title="Reply to start the chat"
      :label-name="`Reply to ${displayName}`"
      :loading="messageSendLoading"
      :default-message="randomDismissalMessage"
      :validation-message="`This field is required`"
      @submit="dismissApplicationWithReply"
      @close="close"
      @close-draft="closeDraft"
    />
    <Button
      value="Dismiss without reply"
      size="medium"
      class="w-full mt-16"
      variant="outlined"
      @click="dismissApplicationWithoutReply"
    />
  </div>
</template>

<script lang="ts">
import SendMessageInput from '@/components/send_message_input/SendMessageInput.vue';
import Button from '@/components/button/Button.vue';
import DiscardPopup from '@/components/send_message_input/DiscardPopup.vue';
import { REPLY_TO_JOB_APPLICATION_WITH_SENDBIRD, UPDATE_JOB_APPLICATION_DISMISSAL } from '@/graphql/applications/mutations';

export default {
  name: 'NotAFitMessageBox',
  components: { SendMessageInput, Button, DiscardPopup },
  inject: {
    navigateToNextApplication: { default: () => {} },
  },
  data() {
    return {
      message: '',
      messageSendLoading: false,
      showDiscardPopup: false,
    };
  },
  computed: {
    displayName(): string {
      return this.$store.getters['applications/applicationDetails'].sitter?.displayName || '';
    },
    currentApplicationUUID(): string {
      return this.$route.params.application_uuid as string;
    },
    randomDismissalMessage(): string {
      const applicantName = this.$store.getters['applications/applicationDetails'].sitter?.firstName || 'Applicant';
      const jobTitle = this.$store.getters['applications/applicationDetails'].job?.title || 'this position';
      const messages = [
        `Hello ${applicantName}! I appreciate your application, but I regret to inform you that we won't be moving forward with your application for the position of "${jobTitle}". Good luck with your job search!`,
        `Hey ${applicantName}! Thanks for applying. I wanted to inform you that we've decided not to proceed with your application for the role of "${jobTitle}". Best of luck in your search!`,
        `Hi, ${applicantName}! I wanted to express my gratitude for your application. However, I must inform you that we won't be continuing with your candidacy for the position of "${jobTitle}". Wishing you the best in your job hunt!`,
        `Hey ${applicantName}, thank you for your application. Unfortunately, I must inform you that we will not be progressing with your application for the position of "${jobTitle}". Best of luck as you continue your job search!`,
        `Hi there, ${applicantName}! I wanted to acknowledge your application and let you know that, regrettably, we will not be moving forward with your application for the position of "${jobTitle}". Good luck in your search!`,
        `Hello ${applicantName}, and thank you for applying. It is with regret that I inform you that we will not be proceeding with your application for the job of "${jobTitle}". Best of luck in your future endeavors!`,
        `Hey ${applicantName}! Thank you for your interest in the "${jobTitle}" position. Unfortunately, we have decided not to proceed with your application. Wishing you the best in your search for new opportunities!`,
        `Hi, ${applicantName}! I wanted to reach out and inform you that we will not be moving forward with your application for the position of "${jobTitle}". Good luck with your search for other opportunities!`,
        `Hey ${applicantName}, thank you for applying for the position of "${jobTitle}". After careful consideration, we have decided not to proceed with your application. Best of luck in your job search!`,
        `Hi ${applicantName}! Thanks for submitting your application for the role of "${jobTitle}". Unfortunately, we won't be proceeding with it. Best of luck with your ongoing job search!`,
        `Hello ${applicantName}! I wanted to personally thank you for applying for the "${jobTitle}" position. However, after careful consideration, we have decided not to move forward with your application. Good luck in your search for the right opportunity!`,
        `Hey ${applicantName}! Thank you for taking the time to apply for the position of "${jobTitle}". I regret to inform you that we will not be progressing with your application at this time. Wishing you all the best in your job search!`,
        `Hi, ${applicantName}! I wanted to express my appreciation for your application for the "${jobTitle}" position. Unfortunately, we will not be proceeding with your candidacy. Best of luck as you continue your search for suitable opportunities!`,
        `Hello ${applicantName}, thank you for your interest in the "${jobTitle}" position. It is with regret that I inform you we will not be moving forward with your application. Good luck with your search for other positions!`,
        `Hi there, ${applicantName}! I wanted to let you know that we have reviewed your application for the position of "${jobTitle}", but unfortunately, we will not be proceeding with it. Best of luck in your search for alternative employment!`,
        `Hello ${applicantName}, and thank you for applying. I regret to inform you that we will not be proceeding with your application for the job of "${jobTitle}". Good luck with your job search!`,
        `Hey ${applicantName}! Thanks for your interest in the "${jobTitle}" role. I'm sorry to say that we won't be moving forward with your application at this time. Best of luck in your search for new opportunities!`,
        `Hi, ${applicantName}! I wanted to express my gratitude for your application for the position of "${jobTitle}". Unfortunately, we have decided not to proceed with your candidacy. Wishing you the best in your job hunt!`,
        `Hello ${applicantName}, thank you for your application. I regret to inform you that we will not be progressing with your application for the position of "${jobTitle}". Best of luck as you continue your job search!`,
        `Hi there, ${applicantName}! I wanted to acknowledge your application and let you know that, unfortunately, we will not be moving forward with your application for the position of "${jobTitle}". Good luck in your search!`,
        `Hello ${applicantName}, and thank you for applying. It is with regret that I inform you that we will not be proceeding with your application for the job of "${jobTitle}". Best of luck in your future endeavors!`,
        `Hey ${applicantName}! Thank you for your interest in the "${jobTitle}" position. Unfortunately, we have decided not to proceed with your application. Wishing you the best in your search for new opportunities!`,
        `Hi, ${applicantName}! I wanted to reach out and inform you that we will not be moving forward with your application for the position of "${jobTitle}". Good luck with your search for other opportunities!`,
        `Hello ${applicantName}, thank you for applying for the position of "${jobTitle}". After careful consideration, we have decided not to proceed with your application. Best of luck in your job search!`,
        `Hi ${applicantName}! Thanks for submitting your application for the role of "${jobTitle}". Unfortunately, we won't be proceeding with it. Best of luck with your ongoing job search!`,
        `Hello ${applicantName}! I wanted to personally thank you for applying for the "${jobTitle}" position. However, after careful consideration, we have decided not to move forward with your application. Good luck in your search for the right opportunity!`,
        `Hey ${applicantName}! Thank you for taking the time to apply for the position of "${jobTitle}". I regret to inform you that we will not be progressing with your application at this time. Wishing you all the best in your job search!`,
        `Hello, ${applicantName}! I wanted to express my appreciation for your application for the "${jobTitle}" position. Unfortunately, we will not be proceeding with your candidacy. Best of luck as you continue your search for suitable opportunities!`,
        `Hi ${applicantName}, thank you for your interest in the "${jobTitle}" position. It is with regret that I inform you we will not be moving forward with your application. Good luck with your search for other positions!`,
        `Hi there, ${applicantName}! I wanted to let you know that we have reviewed your application for the position of "${jobTitle}", but unfortunately, we will not be proceeding with it. Best of luck in your search for alternative employment!`,
      ];

      return messages[Math.floor(Math.random() * messages.length)];
    },
  },
  methods: {
    close(): void {
      this.$emit('close');
      this.showDiscardPopup = false;
      this.message = '';
    },
    closeDraft(): void {
      this.showDiscardPopup = true;
    },
    popupCancel(): void {
      this.showDiscardPopup = false;
    },
    async dismissApplicationWithoutReply() {
      const bannerMessage = 'Applicant moved to not a fit';
      await this.moveApplicationToNotAFit(bannerMessage);
    },
    async dismissApplicationWithReply(message: string) {
      this.messageSendLoading = true;
      await this.replyToJobApplicationWithSendbird(message, this.currentApplicationUUID);
      const bannerMessage = 'Message sent and applicant moved to not a fit';
      await this.moveApplicationToNotAFit(bannerMessage);
      this.messageSendLoading = false;
    },
    async replyToJobApplicationWithSendbird(message: string, jobApplicationId: string): Promise<void> {
      await this.$apollo
        .mutate({
          mutation: REPLY_TO_JOB_APPLICATION_WITH_SENDBIRD,
          variables: {
            jobApplicationId,
            message,
          },
        })
        .catch((error: any) => {
          console.error('GraphQL query error:', error);
        });
    },
    async moveApplicationToNotAFit(bannerMessage: string): Promise<void> {
      await this.$apollo
        .mutate({
          mutation: UPDATE_JOB_APPLICATION_DISMISSAL,
          variables: {
            applicationUuids: [this.currentApplicationUUID],
            dismiss: true,
          },
        })
        .then(async () => {
          const nextApplicationId = await this.$store.dispatch('applications/moveApplication', {
            applicationId: this.currentApplicationUUID,
            fromStatus: 'RECEIVED',
            toStatus: 'NOT_A_FIT',
          });
          this.$store.dispatch('applications/showMessageSentNotification', bannerMessage);
          this.navigateToNextApplication(nextApplicationId);
        })
        .catch((error: any) => {
          console.error('GraphQL query error:', error);
        });
    },
  },
};
</script>
